/**
 * Spacing Tokens
 *
 * Spacing scale system with density support
 * Base 4px scale with multipliers for density levels
 */

// ============================================================================
// DENSITY LEVELS
// ============================================================================

export type DensityLevel = 'compact' | 'comfortable' | 'spacious';

export const DENSITY_MULTIPLIERS = {
  compact: 0.75,      // 25% less spacing
  comfortable: 1,     // Default spacing
  spacious: 1.25,     // 25% more spacing
} as const;

// ============================================================================
// BASE SPACING SCALE (4px base)
// ============================================================================

/**
 * Base spacing scale in pixels
 * Follows 4px grid system
 */
export const BASE_SPACING = {
  0: 0,      // 0px
  1: 4,      // 4px
  2: 8,      // 8px
  3: 12,     // 12px
  4: 16,     // 16px
  5: 20,     // 20px
  6: 24,     // 24px
  8: 32,     // 32px
  10: 40,    // 40px
  12: 48,    // 48px
  16: 64,    // 64px
  20: 80,    // 80px
  24: 96,    // 96px
  32: 128,   // 128px
  40: 160,   // 160px
  48: 192,   // 192px
  56: 224,   // 224px
  64: 256,   // 256px
} as const;

// ============================================================================
// SEMANTIC SPACING
// ============================================================================

/**
 * Semantic spacing values for common use cases
 * Based on base spacing scale
 */
export const SEMANTIC_SPACING = {
  /** Extra small - 4px */
  xs: BASE_SPACING[1],
  /** Small - 8px */
  sm: BASE_SPACING[2],
  /** Medium - 16px */
  md: BASE_SPACING[4],
  /** Large - 24px */
  lg: BASE_SPACING[6],
  /** Extra large - 32px */
  xl: BASE_SPACING[8],
  /** 2XL - 40px */
  '2xl': BASE_SPACING[10],
  /** 3XL - 48px */
  '3xl': BASE_SPACING[12],
} as const;

// ============================================================================
// COMPONENT SPACING
// ============================================================================

/**
 * Spacing for specific component patterns
 */
export const COMPONENT_SPACING = {
  /** Button padding */
  button: {
    sm: { x: BASE_SPACING[3], y: BASE_SPACING[1] },   // 12px x 4px
    md: { x: BASE_SPACING[4], y: BASE_SPACING[2] },   // 16px x 8px
    lg: { x: BASE_SPACING[6], y: BASE_SPACING[3] },   // 24px x 12px
  },

  /** Input padding */
  input: {
    sm: { x: BASE_SPACING[2], y: BASE_SPACING[1] },   // 8px x 4px
    md: { x: BASE_SPACING[3], y: BASE_SPACING[2] },   // 12px x 8px
    lg: { x: BASE_SPACING[4], y: BASE_SPACING[3] },   // 16px x 12px
  },

  /** Card padding */
  card: {
    sm: BASE_SPACING[3],   // 12px
    md: BASE_SPACING[4],   // 16px
    lg: BASE_SPACING[6],   // 24px
  },

  /** Container padding */
  container: {
    sm: BASE_SPACING[4],   // 16px
    md: BASE_SPACING[6],   // 24px
    lg: BASE_SPACING[8],   // 32px
  },

  /** Section spacing */
  section: {
    sm: BASE_SPACING[8],   // 32px
    md: BASE_SPACING[12],  // 48px
    lg: BASE_SPACING[16],  // 64px
  },

  /** Stack spacing (vertical) */
  stack: {
    xs: BASE_SPACING[1],   // 4px
    sm: BASE_SPACING[2],   // 8px
    md: BASE_SPACING[4],   // 16px
    lg: BASE_SPACING[6],   // 24px
  },

  /** Inline spacing (horizontal) */
  inline: {
    xs: BASE_SPACING[1],   // 4px
    sm: BASE_SPACING[2],   // 8px
    md: BASE_SPACING[3],   // 12px
    lg: BASE_SPACING[4],   // 16px
  },
} as const;

// ============================================================================
// LAYOUT SPACING
// ============================================================================

/**
 * Layout-specific spacing values
 */
export const LAYOUT_SPACING = {
  /** Sidebar width */
  sidebar: {
    collapsed: 64,   // 64px
    expanded: 256,   // 256px
  },

  /** Header height */
  header: {
    sm: 48,   // 48px
    md: 64,   // 64px
    lg: 72,   // 72px
  },

  /** Content max width */
  content: {
    sm: 640,    // 640px
    md: 768,    // 768px
    lg: 1024,   // 1024px
    xl: 1280,   // 1280px
    '2xl': 1536, // 1536px
  },

  /** Gutter (grid gap) */
  gutter: {
    xs: BASE_SPACING[2],   // 8px
    sm: BASE_SPACING[4],   // 16px
    md: BASE_SPACING[6],   // 24px
    lg: BASE_SPACING[8],   // 32px
  },
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Apply density multiplier to spacing value
 */
export const applyDensity = (spacing: number, density: DensityLevel = 'comfortable'): number => {
  return Math.round(spacing * DENSITY_MULTIPLIERS[density]);
};

/**
 * Get spacing value with density applied
 */
export const getSpacing = (scale: keyof typeof BASE_SPACING, density: DensityLevel = 'comfortable'): number => {
  return applyDensity(BASE_SPACING[scale], density);
};

/**
 * Convert pixel value to rem
 */
export const pxToRem = (px: number): string => {
  return `${px / 16}rem`;
};

/**
 * Get spacing in rem with density
 */
export const getSpacingRem = (scale: keyof typeof BASE_SPACING, density: DensityLevel = 'comfortable'): string => {
  return pxToRem(getSpacing(scale, density));
};

/**
 * Generate spacing CSS variables with density
 */
export const generateSpacingVars = (density: DensityLevel = 'comfortable'): Record<string, string> => {
  const vars: Record<string, string> = {};

  Object.entries(BASE_SPACING).forEach(([key, value]) => {
    vars[`--spacing-${key}`] = `${applyDensity(value, density)}px`;
  });

  return vars;
};
